import React, { useState } from 'react';
import styled from 'styled-components';
import { formatParams, POST } from 'modules/helpers/fetch';
import { ProductReviewModel } from 'autogen/swagger/all';
import { TrackUAEvent } from 'modules/tracking/UAEventTracking';
import dynamic from 'helpers/dynamic';
import { Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import { WasThisHelpful } from 'autogen/translation-keys/trans-website-productreview-list';
import { useTranslation } from 'react-i18next';
import { InlineButtonWithIcon } from 'components/generic/InlineButton';
import { No, Yes } from 'autogen/translation-keys/trans-website-shared';
import { breakpoint, sizes } from 'variables';
import { Typography } from 'components/generic/typography';
import { IProductReviewListModelUrls } from '../shared/types';
import { useAddReviewLikeMutation } from 'autogen/swagger/all/hooks';

const ThumbsUp = dynamic(() => import('icons/ThumbsUp.svg?react'));
const ThumbsDown = dynamic(() => import('icons/ThumbsDown.svg?react'));

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;

    ${breakpoint.up(sizes.md)} {
        justify-content: flex-start;
    }
`;

const ButtonContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: ${Spacing8};
`;

const VotingLabel = styled(Typography.Body)`
    margin-bottom: 0;
    margin-right: ${Spacing16};
`;

interface ILikeButtonsProps {
    model: ProductReviewModel;
}

export const LikeButtons = (props: ILikeButtonsProps): JSX.Element => {
    const { model, ...rest } = props;
    const {
        likedByCurrentUser: likedByCurrentUserInitial,
        dislikedByCurrentUser: dislikedByCurrentUserInitial,
        likeCount: likeCountInitial,
        dislikeCount: dislikeCountInitial,
        reviewId
    } = model;
    const [likedByCurrentUser, setLikedByCurrentUser] = useState(likedByCurrentUserInitial);
    const [dislikedByCurrentUser, setDislikedByCurrentUser] = useState(dislikedByCurrentUserInitial);
    const [likeCount, setLikeCount] = useState(likeCountInitial);
    const [dislikeCount, setDislikeCount] = useState(dislikeCountInitial);
    const { t } = useTranslation();
    const mutation = useAddReviewLikeMutation();

    const updateButtonState = (state: boolean, isDislike = false) => {
        if (isDislike) {
            if (state) {
                if (likedByCurrentUser && likeCount !== 0) {
                    setLikeCount(likeCount - 1);
                }
                setDislikeCount(dislikeCount + 1);
                setLikedByCurrentUser(false);
            }
            setDislikedByCurrentUser(state);
        } else {
            if (state) {
                if (dislikedByCurrentUser && dislikeCount !== 0) {
                    setDislikeCount(dislikeCount - 1);
                }
                setLikeCount(likeCount + 1);
                setDislikedByCurrentUser(false);
            }
            setLikedByCurrentUser(state);
        }
    };

    const likeReview = async (isDislike = false) => {
        if ((likedByCurrentUser && !isDislike) || (dislikedByCurrentUser && isDislike)) return;

        updateButtonState(true, isDislike);

        try {
            const res = await mutation.mutateAsync({
                params: { productReviewId: reviewId, isDislike }
            }); // has to type res with any temporarily because the response type somehow does not match with BE

            if (!res.isSuccess) {
                throw new Error('Error adding like');
            }
        } catch (e) {
            window.location.reload();
        }
        TrackUAEvent('reviews', 'addLike', `isDislike_${isDislike}`);
    };

    return (
        <Wrapper {...rest}>
            <VotingLabel>{t(WasThisHelpful)}</VotingLabel>
            <ButtonContainer>
                <InlineButtonWithIcon
                    fontWeight={likedByCurrentUser ? 'semibold' : 'regular'}
                    icon={<ThumbsUp />}
                    onClick={() => likeReview(false)}
                >
                    {t(Yes)} <>({likeCount})</>
                </InlineButtonWithIcon>
                <InlineButtonWithIcon
                    fontWeight={dislikedByCurrentUser ? 'semibold' : 'regular'}
                    icon={<ThumbsDown />}
                    onClick={() => likeReview(true)}
                >
                    {t(No)} <>({dislikeCount})</>
                </InlineButtonWithIcon>
            </ButtonContainer>
        </Wrapper>
    );
};

export default LikeButtons;
